import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CustomJwtPayload } from '../models/shared.model';
import { AuthenticationService } from './authentication.service';
import { FeatureFlagService } from './feature-flag.service';
import { SharedService } from './shared.service';
import { SubscriptionService } from './subscription.service';
import { stringConstant } from './stringConstants';
import { environment } from 'src/environments/environment';
import { jwtDecode } from 'jwt-decode';
import { SubscriptionPlan } from '../models/subscription.model';
import { LoaderService } from './loader.service';

@Injectable({
  providedIn: 'root',
})
export class TokenHandlerService {
  public readonly constant: typeof stringConstant;

  constructor(
    private router: Router,
    private toastrService: ToastrService,
    private sharedService: SharedService,
    private authenticationService: AuthenticationService,
    private featureFlagService: FeatureFlagService,
    private subscriptionService: SubscriptionService,
    private loaderService: LoaderService
  ) {
    this.constant = stringConstant;
  }

  /**
   * Process token after successful authentication
   * Handles role-based routing and subscription logic
   * @param token - The JWT token from Auth0
   * @param showLoader - Whether to show/hide loader (true for callback route, false for silent auth)
   */
  processToken(token: string, showLoader: boolean = false): void {
    if (!token) {
      return;
    }
    
    // Store token in localStorage
    this.sharedService.setLocalStorage(this.constant.token, token);
    this.sharedService.setUserLoginStatus();

    // Decode token
    let decodedToken: CustomJwtPayload;
    try {
      decodedToken = jwtDecode(token) as CustomJwtPayload;
    } catch (error) {
      return;
    }

    // Role based redirect of the user
    for (const key of Object.keys(decodedToken)) {
      if (key.includes(this.constant.role)) {
        const userRoles = decodedToken[key];

        if (
          userRoles &&
          userRoles.length &&
          userRoles[0] === this.constant.admin
        ) {
          this.handleAdmin(showLoader);
        } else {
          this.handleUser(showLoader);
        }
        break;
      }
    }
  }

  /**
   * Handle admin user authentication
   * @param showLoader - Whether to show/hide loader
   */
  private handleAdmin(showLoader: boolean): void {
    this.sharedService.setLocalStorage(
      this.constant.userRole,
      this.constant.admin
    );

    if (showLoader) {
      this.loaderService.mainLoaderOff();
    }

    // Check the posthog flag for activate the admin or not
    let featureFlag = '';
    if (environment.env === this.constant.envProduction) {
      featureFlag = this.constant.adminProdFlag;
    } else if (environment.env === this.constant.envStaging) {
      featureFlag = this.constant.adminStageFlag;
    } else if (environment.env === this.constant.envDev) {
      featureFlag = this.constant.adminDevFlag;
    }

    if (featureFlag) {
      this.featureFlagService.getFeatureFlag(featureFlag).subscribe({
        next: (response: boolean) => {
          if (response) {
            this.router.navigate([this.constant.admin]);
          } else {
            this.toastrService.error(
              this.constant.accessPremissionDeniedMessage
            );
            this.authenticationService.auth0Logout();
          }
        },
      });
    } else {
      this.router.navigate([this.constant.admin]);
    }
  }

  /**
   * Handle regular user authentication
   * @param showLoader - Whether to show/hide loader
   */
  private handleUser(showLoader: boolean): void {
    this.sharedService.setLocalStorage(
      this.constant.userRole,
      this.constant.user
    );
    this.checkPlanDetails(showLoader);
  }

  /**
   * Check subscription plan details for user
   * @param showLoader - Whether to show/hide loader
   */
  private checkPlanDetails(showLoader: boolean): void {
    this.subscriptionService.checkSubscriptionPlan().subscribe({
      next: (res: SubscriptionPlan) => {
        if (showLoader) {
          this.loaderService.mainLoaderOff();
        }
        this.subscriptionService.setSubscriptionData(res);
        // If the user landed on the login screen via an invitation link,
        // AcceptInvitePage saved the invite URL here. Honor it ahead of "/"
        // so the invitation is accepted in the same hop.
        const pendingInvite = this.consumePendingInviteUrl();
        if (res.planName === this.constant.free) {
          this.subscriptionService.setPlanId(7);
          this.router.navigateByUrl(pendingInvite ?? '/');
        } else if (res.isActive) {
          this.subscriptionService.setPlanId(res.planId);
          this.router.navigateByUrl(pendingInvite ?? '/');
        } else {
          this.router.navigate([this.constant.profile]);
          this.subscriptionService.setPlanId(-1);
        }
      },
      error: () => {
        this.toastrService.error(
          this.constant.errorFetchingSubscriptionDetails
        );
        if (showLoader) {
          this.loaderService.mainLoaderOff();
        }
        this.authenticationService.auth0Logout();
      },
    });
  }

  /**
   * Read and clear the post-login return URL saved by AcceptInvitePage.
   * Only paths that look like an invite link are honored — anything else
   * is dropped to avoid open-redirect smuggling via localStorage.
   */
  private consumePendingInviteUrl(): string | null {
    try {
      const url = localStorage.getItem('pendingInviteUrl');
      localStorage.removeItem('pendingInviteUrl');
      if (url && /^\/accept-invite\/[A-Za-z0-9]+$/.test(url)) {
        return url;
      }
    } catch {
      // localStorage unavailable — fall back to default landing.
    }
    return null;
  }
}

